/*
 * Keep Horizon's visually clipped variant and purchase controls available as
 * the custom buy box's native state/form bridge, but remove their descendants
 * from keyboard and assistive-technology navigation once that buy box is ready.
 */
(() => {
  const configuredTimeout = Number.parseInt(
    document.currentScript?.dataset.pogiReadinessTimeout || '',
    10,
  );
  const readinessTimeoutMs = Number.isFinite(configuredTimeout) && configuredTimeout > 0
    ? configuredTimeout
    : 4_000;
  const bootstrapStartedAt = Number(window.POGI_BUYBOX_BOOTSTRAP_STARTED_AT);
  const readySections = new WeakSet();
  const readinessTimers = new WeakMap();
  const nativeInteractiveRoots = [
    'variant-picker[ref="mainVariantPicker"]',
    // Apps inject purchase controls beside productFormButtons, including after
    // readiness. Gate their shared hidden parent while retaining its form data.
    '.buy-buttons-block',
    '.buy-buttons-block [ref="productFormButtons"]',
  ];

  const ensureAnnouncementTarget = (buybox) => {
    let target = buybox.querySelector(':scope > [data-pogi-native-status]');
    if (!target) {
      target = document.createElement('div');
      target.className = 'visually-hidden';
      target.setAttribute('data-pogi-native-status', '');
      target.setAttribute('aria-live', 'assertive');
      target.setAttribute('role', 'status');
      target.setAttribute('aria-atomic', 'true');
      const purchaseRow = buybox.querySelector(':scope > .pogi-bb__atc-row');
      if (purchaseRow) purchaseRow.after(target);
      else buybox.appendChild(target);
    }
    return target;
  };

  // The native form still owns all purchase outcomes and keeps its original
  // live-region ref. Relay only its text while that form is inert; the custom
  // target becomes inert with the rest of the custom UI on either fallback.
  const announcementBuybox = (source) => {
    if (!source?.isConnected || !source.closest('.buy-buttons-block[inert]')) return;
    const owner = source.closest('product-form-component');
    if (owner?.querySelector('[ref="liveRegion"]') !== source) return;
    const form = owner.querySelector('form');
    // Product forms contain name="id", which shadows HTMLFormElement.id.
    const formId = form?.getAttribute('id');
    const section = owner.closest('.shopify-section');
    if (!formId || document.getElementById(formId) !== form || !section || section.hasAttribute('data-pogi-buy-box-fallback')) return;
    return Array.from(section.querySelectorAll('.pogi-buy-box[data-ready]')).find((root) => (
      root.dataset.formId === formId && !root.closest('[inert]')
    ));
  };

  const relayNativeAnnouncement = (source) => {
    const buybox = announcementBuybox(source);
    if (!buybox) return;
    const target = ensureAnnouncementTarget(buybox);
    const message = source.textContent || '';
    const error = source.closest('product-form-component').querySelector('[ref="addToCartTextError"]');
    // One status region serves both sighted and assistive-technology users.
    // Only the native form's active error becomes visible; success stays an
    // announcement, with the existing button/drawer owning its visual state.
    const showError = Boolean(message.trim() && error && !error.classList.contains('hidden')
      && error.textContent.includes(message.trim()));
    target.classList.toggle('visually-hidden', !showError);
    target.classList.toggle('pogi-bb__native-error', showError);
    target.textContent = message;
  };

  document.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const owner = event.target.closest('product-form-component');
    const source = owner?.querySelector('[ref="liveRegion"]');
    const buybox = announcementBuybox(source);
    if (!buybox || owner.querySelector('form') !== event.target) return;
    const target = ensureAnnouncementTarget(buybox);
    target.textContent = '';
    target.classList.remove('pogi-bb__native-error');
    target.classList.add('visually-hidden');
  }, true);

  const clearReadinessTimer = (buybox) => {
    const timer = readinessTimers.get(buybox);
    if (timer) clearTimeout(timer);
    readinessTimers.delete(buybox);
  };

  const revealNativeFallback = (buybox) => {
    if (!(buybox instanceof HTMLElement) || buybox.hasAttribute('data-ready') || !buybox.isConnected) return;
    const section = buybox.closest('.shopify-section');
    if (!section) return;

    section.setAttribute('data-pogi-buy-box-fallback', '');
    buybox.setAttribute('inert', '');
    buybox.setAttribute('aria-busy', 'false');
    buybox.dataset.pogiV2Failure = 'readiness timeout';
    buybox.querySelectorAll('[data-pogi-atc]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = true;
    });
    section.querySelectorAll('pogi-sticky-atc').forEach((sticky) => {
      sticky.classList.remove('is-visible');
      sticky.setAttribute('inert', '');
      sticky.setAttribute('aria-hidden', 'true');
      const button = sticky.querySelector('.pogi-satc__btn');
      if (button instanceof HTMLButtonElement) button.disabled = true;
    });
    nativeInteractiveRoots.forEach((selector) => {
      section.querySelectorAll(selector).forEach((root) => root.removeAttribute('inert'));
    });
  };

  const matchingBuyboxes = (root) => {
    const buyboxes = [];
    if (root instanceof Element && root.matches('.pogi-buy-box[data-form-id]:not([data-ready])')) {
      buyboxes.push(root);
    }
    root.querySelectorAll?.('.pogi-buy-box[data-form-id]:not([data-ready])').forEach((buybox) => {
      buyboxes.push(buybox);
    });
    return buyboxes;
  };

  const scheduleReadinessFallbacks = (root = document, useBootstrapDeadline = false) => {
    matchingBuyboxes(root).forEach((buybox) => {
      if (readinessTimers.has(buybox)) return;
      const elapsedBeforeGate = useBootstrapDeadline && Number.isFinite(bootstrapStartedAt)
        ? Math.max(0, performance.now() - bootstrapStartedAt)
        : 0;
      const remainingMs = Math.max(0, readinessTimeoutMs - elapsedBeforeGate);
      const timer = setTimeout(() => {
        readinessTimers.delete(buybox);
        revealNativeFallback(buybox);
      }, remainingMs);
      readinessTimers.set(buybox, timer);
    });
  };

  const gateSection = (section) => {
    if (section.hasAttribute('data-pogi-buy-box-fallback')) {
      nativeInteractiveRoots.forEach((selector) => {
        section.querySelectorAll(selector).forEach((root) => root.removeAttribute('inert'));
      });
      return;
    }
    nativeInteractiveRoots.forEach((selector) => {
      section.querySelectorAll(selector).forEach((root) => {
        root.setAttribute('inert', '');
      });
    });
  };

  const registerBuybox = (buybox) => {
    if (!(buybox instanceof HTMLElement) || !buybox.matches('.pogi-buy-box[data-ready]')) return;
    clearReadinessTimer(buybox);
    const section = buybox.closest('.shopify-section');
    if (!section) return;
    readySections.add(section);
    gateSection(section);
    if (!section.hasAttribute('data-pogi-buy-box-fallback') && !buybox.closest('[inert]')) {
      ensureAnnouncementTarget(buybox);
    }
  };

  document.addEventListener('pogi:buybox-ready', (event) => {
    registerBuybox(event.target);
  });

  document.addEventListener('shopify:product:select', (event) => {
    const section = event.target instanceof Element
      ? event.target.closest('.shopify-section')
      : null;
    if (!section || !readySections.has(section)) return;
    event.promise?.then(() => gateSection(section)).catch(() => {});
  });

  const registerReadyBuyboxes = () => {
    document.querySelectorAll('.pogi-buy-box[data-ready]').forEach(registerBuybox);
    scheduleReadinessFallbacks(document, true);
  };

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => scheduleReadinessFallbacks(node));
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      relayNativeAnnouncement(target?.closest('[ref="liveRegion"]'));
    });
  });
  observer.observe(document.documentElement, { childList: true, characterData: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => {
    scheduleReadinessFallbacks(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerReadyBuyboxes, { once: true });
  } else {
    registerReadyBuyboxes();
  }
})();
